import {
  formatAddressCityState,
  formatDate,
  formatDateTime,
  useInAppNotifications,
  useStoreState,
  rowHover,
  DEFAULT_PER_PAGE,
} from "../../../util/util";
import type {
  IOrderItem,
  IOrdersAPIResponse,
  IPurchaseOrder,
  OrderStatus,
  StatusAndId,
  User,
} from "../../../types/types";
import { IDMobile } from "../../../components/IDs/IDs";
import { Modal } from "../../../components/Modal/Modal";
import { getOrderItemsList } from "../../../util/QuotesAndOrders";
import { useRoutePath } from "../../../util/Routing";
import { useAuthContext } from "../../../components/Auth";
import { PageWrapper } from "../../../layout/portalPageLayout";
import ReactTooltip from "react-tooltip";
import {
  getOrderProductsAsText,
  SellerOrderStatus,
  SellerOrderStatusFilters,
} from "../../../components/OrderStatus/OrderStatus";
import React, { useCallback } from "react";
import { SellerCreateOrder } from "../../Seller/SellerCreateOrder/SellerCreateOrder";
import { useTranslation } from "react-i18next";
import { ConfirmDialog } from "../../../components/ConfirmDialog/ConfirmDialog";
import { calculatePriceDetails } from "../../../components/PriceDetails/PriceDetails";
import { TooltipCell } from "../../Buyer/BuyerOrders/BuyerOrders.util";
import { TrimmedName } from "../../admin/SellerAdmin/PIM/SellarAdminPIMAssets/util/AssetsUtil";
import type { ColumnDef } from "@tanstack/react-table";
import { getAccountManagerCSM, useAccountManagersCSMFilters } from "./util";
import { useListPage } from "../shared";
import type { TFunction } from "react-i18next";
import { TablePageLayout } from "../../../components/TablePage/TablePageLayout";
import { FiltersDropDown } from "../../../components/FiltersDropDown/FiltersDropDown";
import {
  PrimaryButtonWithPlusIcon,
  SecondaryButtonLargeV2,
} from "../../../components/Buttons/Buttons";
import { SlideOut } from "../../../components/SlideOut/SlideOut";
import { DownloadReport } from "./DownloadReport/DownloadReport";

const localStrings = (t: TFunction) => ({
  pageTitle: t("Orders"),
  sellerSearchBarPlaceholder: t("Search by Product, Customers or Order ID"),
  buyerSearchBarPlaceholder: t("Search by Product or Order ID"),
  status: t("Status"),
  perPage: t(" Per Page"),
});

type OrdersTable = {
  id: string;
  products: { items: IOrderItem[]; id: string };
  from: string;
  total_value: string;
  last_activity: string;
  status: StatusAndId<OrderStatus>;
  unread: boolean;
  unread_message_count?: number;
  account_manager_csm: string;
};

/**
 * Shared page component that displays a list of orders as viewed by a seller
 * (as opposed to a buyer).  Used for seller and seller admin.
 */
export const SellerOrdersListPage = () => {
  const { t } = useTranslation();
  const { user, roleIsSomeKindOfSeller } = useAuthContext();
  const { storefront_id } = useStoreState();
  const { notifications } = useInAppNotifications(storefront_id, user as User);
  const { accountPath } = useRoutePath();

  const { accountManagers, customerServiceReps } =
    useAccountManagersCSMFilters("orders");

  // Helper functions
  const getSearchPlaceholder = useCallback(() => {
    if (
      user?.rbac_role === "seller_admin" ||
      user?.rbac_role === "agilis_admin"
    ) {
      return localStrings(t).sellerSearchBarPlaceholder;
    }
    return localStrings(t).buyerSearchBarPlaceholder;
  }, [t, user?.rbac_role]);

  const columns: ColumnDef<OrdersTable>[] = [
    {
      header: t("Order ID"),
      accessorKey: "number",
      id: "number",
      cell: (cell) => <IDMobile>{`${cell.getValue()}`}</IDMobile>,
    },
    {
      header: t("Product(s)"),
      accessorKey: "products",
      id: "products",
      cell: (cell) => {
        return (
          <TooltipCell data-for={`${cell.row.id}-tooltip`} data-tip={""}>
            <TrimmedName
              text={getOrderProductsAsText(
                (
                  cell.getValue() as {
                    items: IOrderItem[];
                    id: string;
                  }
                ).items,
                t
              )}
            />
            <ReactTooltip
              id={`${cell.row.id}-tooltip`}
              place="top"
              data-html={true}
              effect="solid"
              backgroundColor="#60676F"
              multiline={true}
            >
              {getOrderItemsList(
                (
                  cell.getValue() as {
                    items: IOrderItem[];
                    id: string;
                  }
                ).items
              )}
            </ReactTooltip>
          </TooltipCell>
        );
      },
    },
    {
      header: t("From"),
      accessorKey: "from",
      id: "from",
    },
    {
      header: t("Total Value"),
      accessorKey: "total_value",
      id: "total_value",
    },
    {
      header: t("Last Activity"),
      accessorKey: "last_activity",
      id: "last_activity",
    },
    {
      header: t("Order Placed"),
      accessorKey: "created_at",
      id: "created_at",
    },
    { header: t("Shipping Address"), accessorKey: "ship_to", id: "ship_to" },
    {
      header: t("Status"),
      accessorKey: "status",
      id: "status",
      size: 50,
      minSize: 50,
      cell: (cell) => {
        return (
          <SellerOrderStatus
            t={t}
            status={(cell.getValue() as StatusAndId<OrderStatus>).status}
            id={(cell.getValue() as StatusAndId<OrderStatus>).id}
          />
        );
      },
    },
  ];

  const custom_columns: { [accessorKey: string]: number } =
    roleIsSomeKindOfSeller
      ? {
          account_manager_csm: 4,
          unread_message_count: 5,
        }
      : {
          unread_message_count: 5,
        };

  // Data transformation functions
  const dataTransformer = useCallback(
    (response: IOrdersAPIResponse): OrdersTable[] => {
      const getUnread = (order: IPurchaseOrder): boolean => {
        return notifications?.orders.ids
          ? notifications?.orders.ids.includes(order?.id)
          : false;
      };

      return response.data.map((order: IPurchaseOrder) => ({
        id: order.id,
        number: order.number,
        products: { items: order.items, id: order.id },
        from: order.buyer_name || "--",
        total_value: (() => {
          const maybeInvoiceCharges =
            order.invoice && order.invoice.additional_charges
              ? order.invoice.additional_charges
              : [];

          const feesAndCharges = [...order.fees, ...maybeInvoiceCharges];

          const { totalString } = calculatePriceDetails({
            items: order.items,
            fees: feesAndCharges,
            totalDiscount: order.total_discounts,
            currencyCode: order.currency,
          });

          return totalString;
        })(),
        last_activity: formatDateTime(order.modified_at) || "--",
        created_at: formatDate(order.created_at),
        ship_to: order.shipping_address
          ? formatAddressCityState(order.shipping_address)
          : "--",
        status: { status: order.status || "--", id: order.id },
        unread: getUnread(order),
        unread_message_count: order.unread_message_count,
        account_manager_csm: getAccountManagerCSM(order),
      }));
    },
    [notifications?.orders.ids]
  );

  const paginationTransformer = useCallback(
    (response: IOrdersAPIResponse) => ({
      total: response.pagination.total,
      offset: response.pagination.offset,
    }),
    []
  );

  const getUnreadIds = useCallback((order: OrdersTable) => order.unread, []);

  // Use the shared list page hook
  const {
    state,
    data: tableData,
    isLoading,
    error,
    mutate,
    handlers,
    tableColumns,
  } = useListPage({
    defaultPerPage: DEFAULT_PER_PAGE,
    perPageOptions: [10, 20, 50, 100],
    endpoint: `/v1/storefronts/${storefront_id}/orders`,
    dataTransformer,
    paginationTransformer,
    getUnreadIds,
    notifications: notifications?.orders,
    statusFilters: SellerOrderStatusFilters(t),
    accountManagerFilters: accountManagers,
    customerServiceRepFilters: customerServiceReps,
    columns,
    custom_columns,
    basePath: `${accountPath}/orders`,
    searchPlaceholder: getSearchPlaceholder(),
  });

  // Row click handler
  const handleRowClick = (
    e: React.MouseEvent<HTMLTableRowElement, MouseEvent>
  ) => {
    handlers.handleRowClick(e, e.currentTarget.id, `${accountPath}/orders`);
  };

  return (
    <PageWrapper>
      <TablePageLayout
        showTitle
        title={localStrings(t).pageTitle}
        searchQuery={state.searchQuery}
        searchPlaceholder={getSearchPlaceholder()}
        onSearchChange={handlers.handleSearch}
        onSearchClear={handlers.handleClearSearch}
        toolbarActions={
          <>
            <SecondaryButtonLargeV2
              onClick={() => state.setDownloadReportSlideOut(true)}
            >
              {t("Download Report")}
            </SecondaryButtonLargeV2>
            <PrimaryButtonWithPlusIcon
              onClick={() => state.setShowCreateModal(true)}
              useV2
            >
              {t("Create New")}
            </PrimaryButtonWithPlusIcon>
          </>
        }
        filtersSlot={
          <>
            <FiltersDropDown
              activeItems={state.selectedStatus}
              applyStatus={handlers.applyStatus}
              list={SellerOrderStatusFilters(t)}
            />
            {roleIsSomeKindOfSeller && accountManagers.length > 0 && (
              <FiltersDropDown
                activeItems={state.selectedAccountManagers}
                applyStatus={handlers.applyAccountManager}
                list={accountManagers}
                is_status={false}
                header={t("Account Manager")}
              />
            )}
            {roleIsSomeKindOfSeller && customerServiceReps.length > 0 && (
              <FiltersDropDown
                activeItems={state.selectedCustomerServiceReps}
                applyStatus={handlers.applyCustomerServiceRep}
                list={customerServiceReps}
                is_status={false}
                header={t("Customer Success Manager")}
              />
            )}
          </>
        }
        perPage={state.perPage}
        perPageItems={state.perPageItems}
        onPerPageChange={handlers.changePerPage}
        columns={tableColumns}
        data={tableData}
        isLoading={isLoading}
        error={error}
        rowClick={handleRowClick}
        rowHover={rowHover}
        pagination={state.tablePagination}
        totalCount={state.tablePagination.totalCount}
        offset={state.offset}
        onPageChange={handlers.changePage}
      >
        <Modal
          show={state.showCreateModal}
          closeModal={() => state.setShowDialogBox(true)}
          modalWidth={"95%"}
        >
          <SellerCreateOrder
            onSuccess={() => {
              state.setShowCreateModal(false);
              mutate();
            }}
          />
        </Modal>

        <ConfirmDialog
          show={state.showDialogBox}
          confirmMessage={t("Are you sure you want to cancel this order?")}
          closeDialog={() => state.setShowDialogBox(false)}
          handleConfirm={() => {
            state.setShowCreateModal(false);
            state.setShowDialogBox(false);
          }}
        />

        <SlideOut
          closeFlyout={handlers.closeDownloadReportSlideout}
          show={state.downloadReportSlideOut}
          title={t("Download Order Report")}
        >
          <DownloadReport
            closeSlideout={handlers.closeDownloadReportSlideout}
            transactionType="order"
          />
        </SlideOut>
      </TablePageLayout>
    </PageWrapper>
  );
};
